import React, { useState, useCallback, useMemo } from "react"
import DataGrid from "@infotech/uikit-datagrid"
import { wrappedDataGridStyle } from "src/components/DataGridContainer"
import get from "lodash/get"
import { getPlanRedirectUrl, PLANS_URL } from "src/constants/routeNames"
import ModalConfirm from "src/components/modals/ModalConfirm"
import Checkbox from "@infotech/uikit-checkbox"
import { showToast } from "@infotech/uikit-toasts"
import { Box } from "@infotech/uikit-grid"
import getWordByModelName from "src/helpers/text/getWordByModelName"
import * as entity from "src/engine/modules/entity"
import classnames from "classnames"
import { selectValue } from "src/engine/modules/ui/selectors"
import { connect } from "react-redux"
import { deletePlanWithEvents } from "./helper"
import { getDefaultFilter } from "./getDefaultFilter"
import styles from "./styles.css"

const getLineToolsConfig = (
  model,
  baseUri,
  setVisibleConfirmModal,
  history,
  disableDelete,
  deleteFilter,
) => {
  const config = disableDelete
    ? []
    : [
        {
          icon: `trash`,
          onClick: (props, e) => {
            const statusDisable = get(props, `original.restrictions.delete`)
            if (!statusDisable) {
              setVisibleConfirmModal(props.original)
            } else {
              showToast({
                type: `error`,
                message: `Нельзя удалить событие в статусе «‎${get(
                  props,
                  `original.status.name`,
                )}»‎.`,
                hideIn: 5,
              })
            }
            e.stopPropagation()
          },
          filter: deleteFilter,
        },
      ]

  if (model.name === `plan`) {
    config.unshift({
      icon: `edit`,
      onClick: (props, e) => {
        e.stopPropagation()
        const id = get(props.original, model.primaryKey || `id`)
        history.push(`${baseUri}/${id}/main${history.location.search}`)
      },
    })
  }

  return config.length ? config : undefined
}

const alwaysTrue = () => true

export default connect(state => ({
  areMenuCaptionsVisible: selectValue(state, `areMenuCaptionsVisible`),
}))(
  ({
    /* Column according to react-table api */
    columns,
    /* Array of entities according to react-table api  */
    data,
    /* Total count */
    totalCount,
    /* Is something pending (prevent table re-render) */
    pending,
    /* Handle page change */
    pageChangeHandler,
    model,
    baseUri,
    history,
    dispatch,
    /* query for pagination  */
    query,
    setQuery,
    disableDelete,
    canDelete = alwaysTrue,
    areMenuCaptionsVisible,
  }) => {
    const { page, pageSize, orderBy } = query

    const isPlan = model && model.name === `plan`

    const [visibleConfirmModal, setVisibleConfirmModal] = useState(false)
    const [withEvents, changeWithEvents] = useState(false)

    const onRemoveClick = useCallback(() => {
      const id = get(visibleConfirmModal, model.primaryKey || `id`)
      const uuid = get(visibleConfirmModal, `uuid`)
      if (isPlan && withEvents) {
        deletePlanWithEvents({
          uuid,
          id,
          model,
          dispatch,
          name: visibleConfirmModal.name,
        })
      } else {
        dispatch(entity.actions.creators.remove(model, id))
      }
      changeWithEvents(false)
      setVisibleConfirmModal(false)
    }, [model, visibleConfirmModal, withEvents])

    // key форсит обновление, если изменился булеан disableDelete или функцияя canDelete
    // Сделано так потому, что изменение lineTools не вызызывает обновления
    // Например после догрузки полномочий, не появились бы кнопки удаления
    const key = useMemo(Math.random, [canDelete, disableDelete])

    return (
      <div data-testid="entity-list-table">
        <DataGrid
          key={key}
          accumulative
          pending={Boolean(pending)}
          hideUselessPagination
          multiline
          showTextOverflowHintOn="hover"
          totalCount={totalCount}
          withBorder
          pageSize={pageSize}
          className={classnames(wrappedDataGridStyle, {
            [[styles.narrowSidebar]]: !areMenuCaptionsVisible,
            [[styles.wideSidebar]]: areMenuCaptionsVisible,
          })}
          showPageSizeOptions
          onPageSizeChange={pageSize => setQuery({ pageSize, page: 1 })}
          onSortedChange={orderBy => {
            setQuery({
              orderBy: orderBy.length > 0 ? orderBy : undefined,
              page: 1,
            })
          }}
          sorted={orderBy || []}
          manual
          data={data}
          columns={columns}
          page={page - 1 || 0}
          onPageChange={page => {
            setQuery({ page: page + 1 })
            pageChangeHandler(page + 1)
          }}
          getTrProps={(state, rowInfo) => ({
            onClick: () => {
              const id = get(rowInfo.original, model.primaryKey || `id`)
              if (model.name === `plan`) {
                const planUuid = get(rowInfo.original, `uuid`)
                getDefaultFilter({
                  planUuid,
                  history,
                  basicUrl: `${PLANS_URL}/${id}${getPlanRedirectUrl(
                    get(rowInfo.original, `startDateTime`),
                    get(rowInfo.original, `endDateTime`),
                  )}`,
                })
              } else if (model.name === `planType`) {
                history.push(`${baseUri}/${id}/main${history.location.search}`)
              } else {
                history.push(`${baseUri}/${id}${history.location.search}`)
              }
            },
          })}
          withSidesBorder
          lineTools={getLineToolsConfig(
            model,
            baseUri,
            setVisibleConfirmModal,
            history,
            disableDelete,
            ({ original }) => canDelete(original),
          )}
        />
        <ModalConfirm
          isOpen={visibleConfirmModal}
          title={`
            Вы действительно хотите удалить ${getWordByModelName(model.name)}?`}
          buttonConf={[
            {
              text: `Удалить`,
              onClick: onRemoveClick,
              isOk: true,
              done: true,
              disabled: false,
            },
            {
              text: `Отмена`,
              onClick: () => {
                setVisibleConfirmModal(false)
                changeWithEvents(false)
              },
              disabled: false,
            },
          ]}
          isRemove
        >
          <div>
            <Box loose flex column>
              <Box loose>
                {isPlan
                  ? `При удалении плана "${visibleConfirmModal.name}" все его
                события будут отображаться только на календаре.`
                  : visibleConfirmModal.name}
              </Box>
              {isPlan && (
                <Box loose pt="1md">
                  <Checkbox
                    label="Удалить с событиями"
                    checked={withEvents}
                    onCheckedChange={() => changeWithEvents(!withEvents)}
                  />
                </Box>
              )}
            </Box>
          </div>
        </ModalConfirm>
      </div>
    )
  },
)
